import { Button } from "@src/lib/ui/Button";
import { Dialog, DialogContent } from "@src/lib/ui/Dialog";
import XIcon from "@src/lib/icons/12px/x";
import Text from "@src/lib/ui/Text";
import AIUploadArticulateDialog from "@src/features/app/dialogs/ai/AIUploadArticulateDialog";
import { useToast } from "@src/lib/hooks/useToast";
import router from "next/router";
import useFileUploadContextState from "@src/features/libraryItem/hooks/useFileUploadContextState";
import { FileUploadContext } from "@src/lib/contexts/FileUploadContext";
import Sparkles2FillIcon from "@src/lib/icons/18px/sparkles-2-fill";
import { gql } from "@apollo/client";
import {
  useConvertScormPackageMutation,
  useValidateAndExtractScormPackageMutation,
} from "./NewModuleFromArticulateUploadDialog.generated";
import { useCallback, useState } from "react";
import { ScormValidationErrorType } from "@src/types.generated";

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

const getValidationErrorMessage = (
  scormValidationError: ScormValidationErrorType | null | undefined,
): string => {
  switch (scormValidationError) {
    case ScormValidationErrorType.InvalidZipFile:
      return "The uploaded file is not a valid ZIP file. Please upload a valid Articulate Rise 360 export.";
    case ScormValidationErrorType.InvalidArticulateExport:
      return "The uploaded file is not a valid Articulate Rise 360 export. Please ensure you're uploading an exported SCORM package from Articulate Rise 360.";
    default:
      return "unexpected error occurred.";
  }
};

const NewModuleFromArticulateUploadDialog: React.FC<Props> = ({
  open,
  onOpenChange,
}) => {
  const { addErrorToast } = useToast();
  const fileUploadContextState = useFileUploadContextState();
  const uploadedArticulateFileKey = fileUploadContextState.uploadedFileKey;
  const [isValidationSuccessful, setIsValidationSuccessful] = useState(false);

  const [convertSCORMPackage, { loading: convertLoading }] =
    useConvertScormPackageMutation({
      onCompleted: (data) => {
        if (data.convertSCORMPackage.success) {
          const libraryItemId =
            data?.convertSCORMPackage?.scormUpload?.moduleVersion?.module
              .libraryItem.id;
          if (libraryItemId) {
            router.push({
              pathname: "/library/library-item/[id]",
              query: { id: libraryItemId },
            });
          }
          onOpenChange(false);
        } else {
          addErrorToast({
            callsite: "convert_scorm_package",
          });
        }
      },
      onError: () => {
        addErrorToast({
          callsite: "convert_scorm_package",
        });
      },
    });

  const [validateAndExtractScormPackage, { loading: validationLoading }] =
    useValidateAndExtractScormPackageMutation({
      onCompleted: (data) => {
        if (
          data.validateAndExtractScormPackage.success &&
          !data.validateAndExtractScormPackage.scormValidationError
        ) {
          setIsValidationSuccessful(true);
        } else {
          setIsValidationSuccessful(false);
          fileUploadContextState.setUploadedFileKey(null);
          fileUploadContextState.setUploadedFileName(null);
          fileUploadContextState.setUploadedFile(null);
          const errorMessage = getValidationErrorMessage(
            data.validateAndExtractScormPackage?.scormValidationError,
          );
          addErrorToast({
            callsite: "validate_scorm_package",
            message: errorMessage,
          });
        }
      },
      onError: () => {
        setIsValidationSuccessful(false);
        fileUploadContextState.setUploadedFileKey(null);
        fileUploadContextState.setUploadedFileName(null);
        fileUploadContextState.setUploadedFile(null);
        addErrorToast({
          callsite: "validate_scorm_package",
        });
      },
    });

  const handleUploadComplete = useCallback(
    (key: string) => {
      setIsValidationSuccessful(false);
      validateAndExtractScormPackage({ variables: { key } });
    },
    [validateAndExtractScormPackage],
  );

  const loading = validationLoading || convertLoading;
  const isSubmitButtonDisabled =
    loading || !uploadedArticulateFileKey || !isValidationSuccessful;

  return (
    <FileUploadContext.Provider value={fileUploadContextState}>
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent
          className="w-[500px] gap-0 overflow-hidden p-0"
          hideCloseButton
        >
          <div className="flex h-[44px] items-center justify-between border-b border-border p-3 pr-2">
            <Text variant="compact-title">
              New Module from Articulate 360 Rise
            </Text>
            <Button
              variant="secondary"
              size="icon-xs"
              className="text-muted-foreground shadow-none"
              onClick={() => onOpenChange(false)}
            >
              <XIcon />
            </Button>
          </div>
          <div className="flex self-stretch p-2">
            <AIUploadArticulateDialog
              loading={loading}
              isValidating={validationLoading}
              onUploadComplete={handleUploadComplete}
            />
          </div>
          <div className="flex justify-end self-stretch p-2">
            <Button
              loading={loading}
              disabled={isSubmitButtonDisabled}
              onClick={() => {
                if (!uploadedArticulateFileKey) return;
                convertSCORMPackage({
                  variables: { key: uploadedArticulateFileKey },
                });
              }}
            >
              <Sparkles2FillIcon className="text-white" />
              Generate Module
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </FileUploadContext.Provider>
  );
};

export default NewModuleFromArticulateUploadDialog;

gql`
  mutation ValidateAndExtractScormPackage($key: String!) {
    validateAndExtractScormPackage(key: $key) {
      scormUpload {
        id
      }
      scormValidationError
      success
      scormValidationError
      error {
        code
        message
      }
    }
  }
`;

gql`
  mutation ConvertSCORMPackage($key: String!) {
    convertSCORMPackage(key: $key) {
      scormUpload {
        id
        moduleVersion {
          id
          module {
            id
            libraryItem {
              id
            }
          }
        }
      }
      success
      error {
        code
      }
    }
  }
`;
